import React from 'react';
import { Box, Skeleton, Card, useMediaQuery } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';

/**
 * Shared building blocks for the loading skeletons.
 *
 * The goal is that a skeleton occupies the same space and rhythm as the real
 * content it stands in for, so the page does not jump when data arrives.
 */

const GOLD = '#C4B66F';

export const useSkeletonTokens = () => {
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  // Respect the OS setting - a shimmering page is a problem for people with
  // vestibular sensitivity.
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return {
    isDark,
    gold: GOLD,
    deepGreen: '#051814',
    pageBg: isDark ? '#000000' : '#FAFAFA',
    base: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(5, 24, 20, 0.07)',
    highlight: isDark ? 'rgba(196, 182, 111, 0.13)' : 'rgba(196, 182, 111, 0.20)',
    animation: (reducedMotion ? false : 'wave') as 'wave' | false,
    glass: {
      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(20px)',
      border: isDark
        ? '1px solid rgba(196, 182, 111, 0.2)'
        : '1px solid rgba(5, 24, 20, 0.1)',
      boxShadow: isDark
        ? '0 20px 60px rgba(0,0,0,0.7)'
        : '0 20px 60px rgba(5, 24, 20, 0.1)',
    } as SxProps<Theme>,
  };
};

type ShapeProps = {
  w?: number | string;
  h?: number | string;
  r?: number | string;
  sx?: SxProps<Theme>;
};

/** A themed rectangle. Everything else is built from this. */
export const SBlock: React.FC<ShapeProps> = ({ w = '100%', h = 16, r = '8px', sx }) => {
  const t = useSkeletonTokens();
  return (
    <Skeleton
      variant="rectangular"
      animation={t.animation}
      width={w}
      height={h}
      sx={{
        borderRadius: r,
        bgcolor: t.base,
        '&::after': {
          background: `linear-gradient(90deg, transparent, ${t.highlight}, transparent)`,
        },
        ...sx,
      }}
    />
  );
};

export const SCircle: React.FC<{ size?: number; sx?: SxProps<Theme> }> = ({ size = 40, sx }) => (
  <SBlock w={size} h={size} r="50%" sx={{ flexShrink: 0, ...sx }} />
);

/** A run of text lines; the last one is short so it reads like a paragraph. */
export const SText: React.FC<{ lines?: number; h?: number; gap?: number; lastWidth?: string; sx?: SxProps<Theme> }> = ({
  lines = 3,
  h = 12,
  gap = 1,
  lastWidth = '60%',
  sx,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap, width: '100%', ...sx }}>
    {Array.from({ length: lines }).map((_, i) => (
      <SBlock key={i} h={h} w={i === lines - 1 ? lastWidth : '100%'} r="6px" />
    ))}
  </Box>
);

/** Glass card wrapper matching the cards used across the app. */
export const SPanel: React.FC<{ children?: React.ReactNode; p?: number; r?: string; sx?: SxProps<Theme> }> = ({
  children,
  p = 3,
  r = '24px',
  sx,
}) => {
  const t = useSkeletonTokens();
  return (
    <Card sx={{ borderRadius: r, p, width: '100%', minWidth: 0, ...(t.glass as object), ...sx }}>
      {children}
    </Card>
  );
};

/** Avatar + two lines, the shape used for every user/community row. */
export const SRow: React.FC<{ avatar?: number; lines?: number; sx?: SxProps<Theme> }> = ({
  avatar = 40,
  lines = 2,
  sx,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', minWidth: 0, ...sx }}>
    <SCircle size={avatar} />
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <SBlock h={12} w="55%" />
      {lines > 1 && <SBlock h={10} w="35%" />}
    </Box>
  </Box>
);

/**
 * Full-page wrapper. Announces a busy state to assistive tech rather than
 * leaving screen-reader users with a silent, apparently-empty page.
 */
export const SkeletonScreen: React.FC<{ children: React.ReactNode; label?: string; sx?: SxProps<Theme> }> = ({
  children,
  label = 'Loading',
  sx,
}) => {
  const t = useSkeletonTokens();
  return (
    <Box
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      sx={{ minHeight: '100vh', bgcolor: t.pageBg, ...sx }}
    >
      <Box
        component="span"
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
      {children}
    </Box>
  );
};
